import { PHASES, type Phase, type PhaseKey } from '@/modules/phases'

/**
 * Rev A of `product/materials/px-module-flow.pdf` (30 Aug 2026, 4 sheets),
 * transcribed. This module is the prototype's answer to "what is this platform
 * made of" — the L0–L4 module inventory (sheet 4), the three-module loop and
 * its two evidence edges (sheet 1), the seven gated engagement phases
 * (sheet 2), and sheet 3's Run & evolve block, which is NAMED and NOT BUILT.
 *
 * PX-W6.6 (TASK-188E6E40, spec §6.3 item 1) re-homed this module out of
 * `src/demo/`: the Rev A taxonomy is PRODUCT data — the locked shell renders
 * its module attribution on every surface — so product furniture must not
 * reach into the demo module for it (invariant I-2).
 *
 * ⚠ `PhaseKey` comes from `@/modules/phases`. The demo seed stores module ids
 * as plain strings and `architecture.test.ts` asserts they all resolve here,
 * so `src/demo/data.ts` must never import this file back.
 *
 * ⚠ Two lifecycles coexist and NEITHER is renamed into the other. `PHASES`
 * (delivery — the 7 tiles on every project screen) is what the 32 mockup
 * screens are cut by. `ENGAGEMENT_PHASES` (Rev A) is a parallel vocabulary,
 * joined to it by the total function `engagementPhaseFor`.
 */

/* ── Sheet 4 · the module inventory ─────────────────────────────────────── */

export type ModuleLevel = 'L0' | 'L1' | 'L2' | 'L3' | 'L4' | 'cross-cutting'
export type ModuleOrigin = 'sketch' | 'added'

export interface ModuleEntry {
  id: string
  name: string
  level: ModuleLevel
  what: string
  origin: ModuleOrigin
  /** true when the module is installed per client — drives the super-admin matrix columns. */
  perClient: boolean
}

export const MODULE_LEVELS: readonly ModuleLevel[] = [
  'L0',
  'L1',
  'L2',
  'L3',
  'L4',
  'cross-cutting',
] as const

export const LEVEL_LABELS: Readonly<Record<ModuleLevel, string>> = {
  L0: 'L0 · Platform & Admin',
  L1: 'L1 · Organization',
  L2: 'L2 · Client',
  L3: 'L3 · Project / Engagement',
  L4: 'L4 · Execution',
  'cross-cutting': 'Cross-cutting',
}

export const LEVEL_SCOPES: Readonly<Record<ModuleLevel, string>> = {
  L0: 'tenant-wide',
  L1: 'the firm',
  L2: 'thin in the sketch',
  L3: 'your project cards',
  L4: 'agentic runtime',
  'cross-cutting': 'all levels',
}

/**
 * The 44 live modules, enumerated from sheet 4 at full resolution.
 * See `REV_A_RECONCILIATION` for why this is 44 and the footer says 45.
 */
export const MODULE_REGISTRY: readonly ModuleEntry[] = [
  // L0 · Platform & Admin — 8 live (3 sketch, 5 added)
  { id: 'access-manager', name: 'Access Manager', level: 'L0', what: 'Users, roles, scopes — your "access manager".', origin: 'sketch', perClient: false },
  { id: 'agent-module-registry', name: 'Agent & Module Registry', level: 'L0', what: 'What can be installed and run.', origin: 'sketch', perClient: false },
  { id: 'template-library', name: 'Template Library', level: 'L0', what: 'Project, deliverable and prompt templates.', origin: 'sketch', perClient: true },
  { id: 'tenant-provisioning', name: 'Tenant Provisioning', level: 'L0', what: 'Spin up a new firm with defaults and seed data.', origin: 'added', perClient: false },
  { id: 'model-gateway', name: 'Model Gateway', level: 'L0', what: 'Model choice, routing, fallback, key management.', origin: 'added', perClient: false },
  { id: 'guardrails-policy', name: 'Guardrails & Policy', level: 'L0', what: 'Data classification, EU AI Act / GDPR, DPIA records.', origin: 'added', perClient: false },
  { id: 'audit-log', name: 'Audit Log', level: 'L0', what: 'Immutable who-did-what, incl. every agent action.', origin: 'added', perClient: false },
  { id: 'metering-billing', name: 'Metering & Billing', level: 'L0', what: 'Seats, runs, tokens → invoice.', origin: 'added', perClient: false },

  // L1 · Organization — 9 live (6 sketch, 3 added)
  { id: 'organization-profile', name: 'Organization Profile', level: 'L1', what: 'Legal entity, branding, defaults.', origin: 'sketch', perClient: false },
  { id: 'people-roles', name: 'People & Roles', level: 'L1', what: 'Your "HR" — consultants, managers, owner.', origin: 'sketch', perClient: false },
  { id: 'contracts', name: 'Contracts', level: 'L1', what: 'Client and employment agreements.', origin: 'sketch', perClient: false },
  { id: 'client-portfolio', name: 'Client Portfolio', level: 'L1', what: 'Master list, account owner per client.', origin: 'sketch', perClient: false },
  { id: 'budget', name: 'Budget', level: 'L1', what: 'Annual and per-client targets.', origin: 'sketch', perClient: false },
  { id: 'results', name: 'Results', level: 'L1', what: 'Revenue and margin, rolled up from L3.', origin: 'sketch', perClient: false },
  { id: 'rates-capacity', name: 'Rates & Capacity', level: 'L1', what: 'Rate cards and availability — budget vs actual needs it.', origin: 'added', perClient: false },
  { id: 'firm-knowledge-base', name: 'Firm Knowledge Base', level: 'L1', what: 'Methodology, IP, past deliverables.', origin: 'added', perClient: false },
  { id: 'integrations', name: 'Integrations', level: 'L1', what: 'Odoo core, mail, calendar, accounting, storage.', origin: 'added', perClient: false },

  // L2 · Client — 5 live (1 sketch, 4 added) — all per-client
  { id: 'client-profile', name: 'Client Profile', level: 'L2', what: 'Account record, contacts, commercial terms.', origin: 'sketch', perClient: true },
  { id: 'client-data-room', name: 'Client Data Room', level: 'L2', what: 'Documents and source systems scoped to one client.', origin: 'added', perClient: true },
  { id: 'client-context-graph', name: 'Client Context Graph', level: 'L2', what: 'Indexed knowledge the expert agents read.', origin: 'added', perClient: true },
  { id: 'access-sharing', name: 'Access & Sharing', level: 'L2', what: 'External portal, per-project visibility, watermarking.', origin: 'added', perClient: true },
  { id: 'client-pl', name: 'Client P&L', level: 'L2', what: 'Margin per account — the missing rung to firm results.', origin: 'added', perClient: true },

  // L3 · Project / Engagement — 9 live (5 sketch, 4 added) — all per-client
  { id: 'project-workspace', name: 'Project Workspace', level: 'L3', what: 'One engagement: scope, team, status.', origin: 'sketch', perClient: true },
  { id: 'work-packages', name: 'Work Packages', level: 'L3', what: 'The small boxes inside each project card.', origin: 'sketch', perClient: true },
  { id: 'hours-timesheets', name: 'Hours / Timesheets', level: 'L3', what: 'Per consultant, per work package.', origin: 'sketch', perClient: true },
  { id: 'deliverables', name: 'Deliverables', level: 'L3', what: 'Your "results" diamonds — client-facing output.', origin: 'sketch', perClient: true },
  { id: 'consultant-assignment', name: 'Consultant Assignment', level: 'L3', what: 'Your "consultant / employee" lane.', origin: 'sketch', perClient: true },
  { id: 'budget-vs-actual', name: 'Budget vs Actual', level: 'L3', what: 'Fills your first "?" tile. Hours × rate vs sold value.', origin: 'added', perClient: true },
  { id: 'margin-utilization', name: 'Margin & Utilization', level: 'L3', what: 'Fills your second "?" tile. Includes agent run cost.', origin: 'added', perClient: true },
  { id: 'project-agent-crew', name: 'Project Agent Crew', level: 'L3', what: 'Which experts run here, with what permissions.', origin: 'added', perClient: true },
  { id: 'deliverable-versioning', name: 'Deliverable Versioning', level: 'L3', what: 'Draft → review → approved → sent.', origin: 'added', perClient: true },

  // L4 · Execution — 8 live (3 sketch, 5 added)
  { id: 'triggers-scheduler', name: 'Triggers & Scheduler', level: 'L4', what: 'Manual, scheduled or event-driven.', origin: 'sketch', perClient: false },
  { id: 'expert-agents', name: 'Expert Agents', level: 'L4', what: 'Your info / data / tech — as roles, not fixed builds.', origin: 'sketch', perClient: true },
  { id: 'results-store', name: 'Results Store', level: 'L4', what: 'Raw agent output before review.', origin: 'sketch', perClient: false },
  { id: 'orchestrator', name: 'Orchestrator', level: 'L4', what: 'Sequences agents, retries, state, hand-offs.', origin: 'added', perClient: false },
  { id: 'tools-connectors', name: 'Tools & Connectors', level: 'L4', what: 'MCP / API tools an agent may call, scoped per client.', origin: 'added', perClient: true },
  { id: 'human-review-gate', name: 'Human Review Gate', level: 'L4', what: 'Approve, edit or reject before a client sees it.', origin: 'added', perClient: false },
  { id: 'run-log-cost', name: 'Run Log & Cost', level: 'L4', what: 'Per run, tokens and euros. Feeds L3 margin.', origin: 'added', perClient: false },
  { id: 'evaluation-feedback', name: 'Evaluation & Feedback', level: 'L4', what: 'Score output, promote what works into templates.', origin: 'added', perClient: false },

  // Cross-cutting — 5 live (0 sketch, 5 added)
  { id: 'notifications-inbox', name: 'Notifications & Inbox', level: 'cross-cutting', what: 'Where a human is asked to act.', origin: 'added', perClient: false },
  { id: 'search', name: 'Search', level: 'cross-cutting', what: 'Across all levels, permission-aware.', origin: 'added', perClient: false },
  { id: 'analytics-dashboards', name: 'Analytics & Dashboards', level: 'cross-cutting', what: 'One reporting layer, not per-level charts.', origin: 'added', perClient: false },
  { id: 'permission-inheritance', name: 'Permission Inheritance', level: 'cross-cutting', what: 'Org → client → project → run. One rule set.', origin: 'added', perClient: false },
  { id: 'retention-compliance', name: 'Retention & Compliance', level: 'cross-cutting', what: 'Data residency, deletion, export per client.', origin: 'added', perClient: false },
] as const

export const moduleById = (id: string): ModuleEntry => {
  const m = MODULE_REGISTRY.find((x) => x.id === id)
  if (!m) throw new Error(`Unknown module: ${id}`)
  return m
}

export const modulesAtLevel = (level: ModuleLevel): readonly ModuleEntry[] =>
  MODULE_REGISTRY.filter((m) => m.level === level)

export const perClientModules = (): readonly ModuleEntry[] =>
  MODULE_REGISTRY.filter((m) => m.perClient)

export interface RemovedModule {
  label: string
  level: ModuleLevel
  why: string
}

/** Sheet 4's removed / reclassified column. These are NOT live modules. */
export const REMOVED_MODULES: readonly RemovedModule[] = [
  { label: 'Unnamed tile', level: 'L0', why: 'Empty box beside "agent / module". Nothing to build yet.' },
  { label: '"Many / card"', level: 'L1', why: 'Ambiguous tile. Folded into Client Portfolio.' },
  { label: '"Etc." tile', level: 'L1', why: 'Placeholder. Replaced with named modules.' },
  { label: '"Same for OB"', level: 'L2', why: 'Not a module. Tenancy, solved once at L0.' },
  { label: '"Project All"', level: 'L3', why: 'A portfolio view over L3, not a module.' },
  { label: 'Two "?" tiles', level: 'L3', why: 'Replaced by the two modules above.' },
  { label: 'Fixed expert list', level: 'L4', why: 'Four hardcoded modules → registry entries instead.' },
] as const

/** The only three values a super-admin matrix cell may render. */
export type ModuleState = 'installed' | 'available' | 'not-licensed'
export const MODULE_STATES: readonly ModuleState[] = [
  'installed',
  'available',
  'not-licensed',
] as const

export const REV_A = {
  rev: 'A',
  dated: '2026-08-30',
  sheets: 4,
  /** The sheet-4 footer, VERBATIM. It does not reconcile — see below. */
  totals: { modules: 45, kept: 20, added: 25, removed: 7 },
} as const

/**
 * The delta is real and is RENDERED rather than papered over: sheet 4
 * enumerates 44 live rows against a footer claiming 45, and the enumerated
 * origin split is 18 / 26 against a claimed 20 / 25. `removed` reconciles
 * exactly at 7. No row is invented to close the gap and none is dropped.
 *
 * Independently corroborated: `px-onboarding-flow.html` renders the same
 * framework as 44 module items (Admin 16 · Org 9 · Client 14 · cross 5).
 */
export const REV_A_RECONCILIATION = {
  transcribed: { modules: 44, sketch: 18, added: 26, removed: 7 },
  footer: REV_A.totals,
  delta: { modules: 1, kept: 2, added: -1, removed: 0 },
  note: 'Sheet 4 enumerates 44 live modules (18 from-sketch + 26 added); the sheet footer states 45 · 20 kept · 25 added. Removed reconciles exactly at 7. The inventory below is the enumerated set, transcribed at full resolution; no row is invented to close the gap.',
} as const

/* ── Sheet 2 · the seven gated engagement phases ────────────────────────── */

export type EngagementPhaseKey =
  | 'setup'
  | 'prep'
  | 'discovery'
  | 'design'
  | 'build'
  | 'validate-deliver'
  | 'close-learn'

export interface EngagementPhase {
  key: EngagementPhaseKey
  number: 1 | 2 | 3 | 4 | 5 | 6 | 7
  name: string
  purpose: string
  /** The gate's NAME is Rev A's; the KIND of sign-off is the delivery ribbon's. */
  gateName: string
  gateCriteria: string
  /** Agent-leverage dots, 0–5. */
  leverage: 0 | 1 | 2 | 3 | 4 | 5
  /** Delivery phases (the 32 screens' cut) that realise this phase; may be empty. */
  deliveryPhases: readonly PhaseKey[]
}

export const ENGAGEMENT_PHASES: readonly EngagementPhase[] = [
  { key: 'setup', number: 1, name: 'Setup', purpose: 'Create the environment. Once per firm.', gateName: 'SETUP DONE', gateCriteria: 'Environment live, roles assigned, policy and model routing set.', leverage: 1, deliveryPhases: [] },
  { key: 'prep', number: 2, name: 'Prep', purpose: 'Create the project, staff the team.', gateName: 'PREP DONE', gateCriteria: 'Scope signed, team staffed, budget loaded, data room open.', leverage: 2, deliveryPhases: [] },
  { key: 'discovery', number: 3, name: 'Discovery', purpose: 'Gather and understand the client.', gateName: 'DISCOVERY DONE', gateCriteria: 'Context graph indexed, as-is baseline agreed with client.', leverage: 5, deliveryPhases: ['discover'] },
  { key: 'design', number: 4, name: 'Design', purpose: 'Shape and cost the solution.', gateName: 'DESIGN DONE', gateCriteria: 'Design approved, options costed, budget still within tolerance.', leverage: 3, deliveryPhases: ['design'] },
  { key: 'build', number: 5, name: 'Build', purpose: 'Produce the actual work.', gateName: 'BUILD DONE', gateCriteria: 'Work packages complete, agent cost inside the run budget.', leverage: 4, deliveryPhases: ['build'] },
  { key: 'validate-deliver', number: 6, name: 'Validate & deliver', purpose: 'Review, approve, hand over.', gateName: 'ACCEPTED', gateCriteria: 'Client acceptance signed, deliverables versioned and shared.', leverage: 2, deliveryPhases: ['migrate', 'test', 'go-live'] },
  { key: 'close-learn', number: 7, name: 'Close & learn', purpose: 'Invoice, measure, improve.', gateName: 'CLOSED', gateCriteria: 'Invoiced, margin known, templates and agents updated.', leverage: 4, deliveryPhases: ['maintain'] },
] as const

export const MAX_LEVERAGE = 5

/** Sheet-2 framing, rendered on /architecture. */
export const SHEET_2_FRAMING =
  'Phases 01 and 02 happen once per firm and once per project. Phases 03 to 06 repeat per work package. Phase 07 is the only one that makes the next engagement cheaper — it hands over to Run (sheet 3) and back to phase 01 of the next engagement: better templates, better agents, reusable IP.'

export const SHEET_2_RISK =
  'Phase 05 is where agent cost can quietly eat the margin. Run Log & Cost has to exist before Build is automated, not after.'

/**
 * The crosswalk, in the only direction that can be total: every delivery phase
 * has exactly one engagement phase. It is deliberately NOT injective — Rev A's
 * phase 06 legitimately covers Migrate, Test and Go-Live. Throws rather than
 * returning undefined, so a hole in the map is a loud failure, not a
 * `| undefined` leaking into every caller.
 */
export const engagementPhaseFor = (delivery: PhaseKey): EngagementPhase => {
  const phase = ENGAGEMENT_PHASES.find((p) => p.deliveryPhases.includes(delivery))
  if (!phase) throw new Error(`No engagement phase realises delivery phase: ${delivery}`)
  return phase
}

export type GateKind = Phase['gate']

/**
 * Gate STATUS vocabulary derives from the delivery ribbon's existing
 * `PHASES[phase].gate`, so nothing new is seeded: the gate's NAME is Rev A's,
 * the KIND of sign-off is the ribbon's. This is the ONE home for these three
 * strings — the ribbon, the boards and the portfolio rows all read them here.
 */
export const GATE_STATUS_LABELS: Record<GateKind, string> = {
  'Client sign-off': 'Open · client sign-off',
  Internal: 'Open · internal sign-off',
  Quarterly: 'Open · quarterly review',
}

/** A phase with no gate kind reads a bare 'Open' rather than inventing one. */
export const gateStatusLabelFor = (phase: PhaseKey): string => {
  const gate = PHASES.find((p) => p.key === phase)?.gate
  return gate ? GATE_STATUS_LABELS[gate] : 'Open'
}

export const gateIsClientFacingFor = (phase: PhaseKey): boolean =>
  PHASES.find((p) => p.key === phase)?.gate === 'Client sign-off'

/* ── Sheet 1 · the three modules and the loop ───────────────────────────── */

export interface ModuleFlowNode {
  key: 'organization' | 'admin' | 'client'
  title: string
  caption: string
  subtitle: string
  groups: readonly { heading: string; items: readonly string[] }[]
}

export const MODULE_FLOW_CAPTION =
  'Admin builds the capability. Organization allocates it. Client is where it turns into billable value — and the evidence flows back the other way.'

export const MODULE_FLOW: readonly ModuleFlowNode[] = [
  {
    key: 'organization',
    title: 'ORGANIZATION',
    subtitle: 'Perpleks — the firm',
    caption: 'Who we are, who we sell to, and what it must earn.',
    groups: [
      { heading: 'Firm & people', items: ['Organization Profile', 'People & Roles (HR)', 'Contracts', 'Firm Knowledge Base', 'Integrations'] },
      { heading: 'Commercial', items: ['Client Portfolio', 'Budget', 'Results', 'Rates & Capacity'] },
    ],
  },
  {
    key: 'admin',
    title: 'ADMIN',
    subtitle: 'Agents & platform',
    caption: 'What the platform can do, and the rules it does it under. Built once, used by every client.',
    groups: [
      { heading: 'Platform', items: ['Access Manager', 'Agent & Module Registry', 'Template Library', 'Tenant Provisioning', 'Model Gateway', 'Guardrails & Policy', 'Audit Log', 'Metering & Billing'] },
      { heading: 'Runtime', items: ['Triggers & Scheduler', 'Expert Agents', 'Results Store', 'Orchestrator', 'Tools & Connectors', 'Human Review Gate', 'Run Log & Cost', 'Evaluation & Feedback'] },
    ],
  },
  {
    key: 'client',
    title: 'CLIENT',
    subtitle: 'Accounts & projects',
    caption: 'Where the work actually happens. One account, many engagements, all of it billable.',
    groups: [
      { heading: 'Account', items: ['Client Profile', 'Client Data Room', 'Client Context Graph', 'Access & Sharing', 'Client P&L'] },
      { heading: 'Engagement', items: ['Project Workspace', 'Work Packages', 'Hours / Timesheets', 'Deliverables', 'Consultant Assignment', 'Budget vs Actual', 'Margin & Utilization', 'Project Agent Crew', 'Deliverable Versioning'] },
    ],
  },
] as const

/** The two green edges — the evidence that flows back up the loop. */
export const EVIDENCE_EDGES: readonly { from: string; to: string; label: string }[] = [
  { from: 'Client', to: 'Organization', label: 'hours · margin · client P&L' },
  { from: 'Client', to: 'Admin', label: 'run logs · eval scores · cost' },
] as const

export const LOOP_NOTE =
  'Admin ships a capability. Organization decides which client gets it and at what rate. Client work produces evidence — cost, margin, quality scores. That evidence returns to Admin as better agents and to Organization as real results.'

/* ── Sheet 3 · Run & evolve — NAMED, NOT BUILT ──────────────────────────── */

export const RUN_AND_EVOLVE = {
  inScope: false,
  sheet: 3,
  modules: [
    { id: 'M01', name: 'Context currency', cadence: 'continuous', owner: 'Admin + Client' },
    { id: 'M02', name: 'Tailoring & front end', cadence: 'on demand', owner: 'Perpleks build team' },
    { id: 'M03', name: 'Maintenance & security', cadence: 'continuous + quarterly', owner: 'Perpleks platform team' },
  ],
  why: 'Implementation ends at phase 07; the platform does not. These three keep it running, and two of them pull against each other, so the trade has to be priced deliberately. The prototype names them and builds none of them.',
} as const

/* ── Surface → module attribution (AC-6.4) ──────────────────────────────── */

/**
 * Route pattern → the module ids that surface realises. A route absent from
 * this map renders NO attribution row — never a placeholder, never "unknown".
 */
export const SURFACE_MODULES: Readonly<Record<string, readonly string[]>> = {
  // `/` is deliberately absent: it RENDERS the current role's landing, so the
  // shell resolves the attribution against `landingRouteFor(role)` instead —
  // otherwise the same rendered page would declare different modules at `/`
  // and at its own address.
  // `/home` is absent too (TASK-505148A8): Home renders only the module
  // overview cards and realises none of the Rev A catalogue modules.
  '/login': ['access-manager', 'permission-inheritance'],
  '/designs': ['template-library'],
  '/portal': ['access-sharing', 'deliverables'],

  '/architecture': ['agent-module-registry', 'analytics-dashboards'],

  '/my/board': ['work-packages', 'consultant-assignment', 'hours-timesheets'],
  '/my/project': ['access-sharing', 'work-packages', 'deliverables'],
  '/my/team': ['people-roles', 'rates-capacity', 'project-workspace', 'work-packages'],
  '/my/client': ['client-profile', 'access-sharing', 'project-workspace'],

  '/admin/projects': ['client-portfolio', 'people-roles', 'project-workspace', 'consultant-assignment'],
  '/admin/clients': ['access-manager', 'agent-module-registry', 'tenant-provisioning', 'client-profile'],
  // PX-W6.5 (TASK-AAB07DC3): the configuration surfaces.
  '/admin/modules': ['agent-module-registry', 'access-manager'],
  '/admin/connectors': ['tools-connectors', 'integrations'],
  '/admin/connectors/callback': ['tools-connectors'],
  '/admin/runtime': ['model-gateway', 'triggers-scheduler', 'client-context-graph'],

  '/resources/pool': ['people-roles', 'rates-capacity'],
  '/resources/people/:personId': ['people-roles'],
  '/resources/capacity': ['rates-capacity', 'analytics-dashboards'],
  '/resources/allocations': ['consultant-assignment', 'rates-capacity'],
  '/resources/skills': ['people-roles'],
  '/resources/utilisation': ['margin-utilization', 'analytics-dashboards'],
  '/resources/pipeline': ['people-roles'],
  '/resources/training': ['firm-knowledge-base', 'people-roles'],
  '/resources/badges': ['people-roles', 'firm-knowledge-base'],

  '/projects': ['client-portfolio', 'project-workspace'],
  '/projects/health': ['results', 'analytics-dashboards'],
  '/clients': ['client-portfolio', 'client-profile', 'contracts'],

  '/library/templates': ['template-library'],
  '/library/templates/:templateId': ['template-library'],
  '/library/requirements': ['firm-knowledge-base', 'search'],
  '/library/agents': ['agent-module-registry', 'expert-agents'],
  '/library/agents/:agentId': ['expert-agents', 'model-gateway'],
  '/library/agents/:agentId/guardrails': ['guardrails-policy', 'human-review-gate'],
  '/library/blueprints': ['template-library', 'firm-knowledge-base'],
  '/library/playbooks': ['template-library', 'triggers-scheduler'],

  '/project/:projectId': ['project-workspace', 'budget-vs-actual'],
  '/project/:projectId/interviews': ['client-context-graph', 'expert-agents'],
  '/project/:projectId/requirements': ['client-context-graph', 'work-packages'],
  '/project/:projectId/blueprint': ['deliverables', 'deliverable-versioning'],
  '/project/:projectId/build': ['work-packages', 'human-review-gate', 'run-log-cost'],
  '/project/:projectId/migration': ['client-data-room', 'tools-connectors', 'human-review-gate'],
  '/project/:projectId/test': ['work-packages', 'expert-agents'],
  '/project/:projectId/go-live': ['project-workspace', 'orchestrator'],
  '/project/:projectId/ams': ['contracts', 'triggers-scheduler'],
}

/** Longest pattern first, computed once — the map is static. */
const SORTED_PATTERNS = Object.keys(SURFACE_MODULES).sort(
  (a, b) => b.split('/').length - a.split('/').length
)

const segmentsMatch = (pattern: string, pathname: string): boolean => {
  const p = pattern.split('/').filter(Boolean)
  const a = pathname.split('/').filter(Boolean)
  if (p.length !== a.length) return false
  return p.every((seg, i) => seg.startsWith(':') || seg === a[i])
}

/**
 * The modules a pathname realises. Matches the LONGEST pattern first so
 * `/project/:projectId/build` wins over `/project/:projectId`, and returns an
 * empty list — never a placeholder — for an unmapped route.
 */
export const modulesForPath = (pathname: string): readonly ModuleEntry[] => {
  const clean = pathname.split('?')[0]?.split('#')[0] ?? ''
  const normalised = clean.length > 1 ? clean.replace(/\/+$/, '') : clean
  const hit = SORTED_PATTERNS.find((pattern) => segmentsMatch(pattern, normalised))
  if (!hit) return []
  return (SURFACE_MODULES[hit] ?? []).map(moduleById)
}
